import { useMemo, useState } from 'react'
import {
  Field,
  FieldArray,
  Form,
  Formik,
  getIn,
  useFormikContext
} from 'formik'
import { JSONTree } from 'react-json-tree'
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid'

import Modal from '../Modal'
import Button from '../Button'
import { classNames, extractErrorMessage } from '../../helper'
import {
  KIND,
  KIND_LABELS,
  LEAD_AMPEL,
  METRICS,
  METRIC_LABELS,
  ROLLOUT_VARIANTS,
  STAGES,
  SURFACES,
  SURFACE_LABELS,
  UNITS,
  UNIT_LABELS,
  buildFlagSchema,
  emptyFormValues,
  flagToFormValues,
  formValuesToFlag,
  formatPercent,
  isLocked,
  normalizedWeights
} from '../../helper/experiments'
import { PricingWriteCancelledError } from '../../helper/pricing-flag'
import { useNotificationContext } from '../../context/notification-context'
import {
  CONFLICT_MESSAGE,
  FlagsConflictError
} from '../../hooks/use-feature-flags'
import RuntimeEstimator from './RuntimeEstimator'
import { LockNotice, Notice, StatusBadge } from './ExperimentBadges'
import { Explain, HELP } from './Help'

const inputClass =
  'block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'
const labelClass = 'block text-xs font-medium text-gray-700 mb-1'
const hintClass = 'mt-1 text-xs text-gray-500'
const checkboxClass =
  'h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600'
const iconButtonClass =
  'rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-40'
const addButtonClass =
  'inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-40'

const UNIT_ID_LABELS = {
  visitor: 'Besucher-ID (aivy_vid)',
  partner: 'Partner-ID',
  user: 'User-ID'
}

// Zeigt nur String-Fehler. Formik legt Array-Fehler je Eintrag als Objekte
// ab, die hier nicht gerendert werden dürfen.
const FieldError = ({ name }) => {
  const { errors, touched, submitCount } = useFormikContext()
  const message = getIn(errors, name)
  if (typeof message !== 'string') return null
  if (!getIn(touched, name) && submitCount === 0) return null
  return <p className='mt-1 text-xs text-red-600'>{message}</p>
}

const Section = ({ title, description, help, children }) => (
  <section className='border-t border-gray-200 pt-5'>
    <h4 className='text-sm font-semibold text-gray-900'>{title}</h4>
    {description && <p className={hintClass}>{description}</p>}
    {help && <Explain className='mt-2'>{help}</Explain>}
    <div className='mt-3 space-y-4'>{children}</div>
  </section>
)

const KeyField = () => {
  const { values, setFieldValue } = useFormikContext()
  return (
    <Field name='key'>
      {({ field }) => (
        <input
          {...field}
          id='key'
          className={classNames(inputClass, 'font-mono')}
          placeholder='z. B. funnel_email_position'
          autoComplete='off'
          onChange={(e) => {
            const next = e.target.value
            // Salt folgt dem Key, solange niemand ihn eigens gesetzt hat.
            if (!values.salt || values.salt === values.key) {
              setFieldValue('salt', next)
            }
            setFieldValue('key', next)
          }}
        />
      )}
    </Field>
  )
}

const VariantsField = ({ locked }) => {
  const { values } = useFormikContext()
  const shares = normalizedWeights(values.variants)

  return (
    <FieldArray name='variants'>
      {({ push, remove }) => (
        <div className='space-y-2'>
          <div className='hidden gap-2 text-xs font-medium text-gray-500 sm:flex'>
            <span className='w-20' />
            <span className='flex-1'>Key</span>
            <span className='w-28'>Gewicht</span>
            <span className='w-14 text-right'>Anteil</span>
            <span className='w-8' />
          </div>
          {values.variants.map((variant, index) => (
            <div key={index} className='flex flex-wrap items-start gap-2'>
              <span className='w-20 pt-2 text-xs font-medium text-gray-500'>
                {index === 0 ? 'Control' : `Variante ${index}`}
              </span>
              <div className='min-w-[10rem] flex-1'>
                <Field
                  name={`variants.${index}.key`}
                  className={classNames(inputClass, 'font-mono')}
                  placeholder={index === 0 ? 'control' : 'z. B. email_last'}
                  disabled={locked}
                  aria-label={`Key der Variante ${index + 1}`}
                />
                <FieldError name={`variants.${index}.key`} />
              </div>
              <div className='w-28'>
                <Field
                  type='number'
                  min='0'
                  step='any'
                  name={`variants.${index}.weight`}
                  className={inputClass}
                  disabled={locked}
                  aria-label={`Gewicht der Variante ${index + 1}`}
                />
                <FieldError name={`variants.${index}.weight`} />
              </div>
              <span className='w-14 pt-2 text-right text-xs text-gray-500'>
                {formatPercent(shares[index]?.share, 0)}
              </span>
              <button
                type='button'
                className={iconButtonClass}
                onClick={() => remove(index)}
                disabled={locked || values.variants.length <= 2}
                title='Variante entfernen'
              >
                <TrashIcon className='h-4 w-4' aria-hidden='true' />
                <span className='sr-only'>Variante entfernen</span>
              </button>
            </div>
          ))}
          <FieldError name='variants' />
          <button
            type='button'
            className={addButtonClass}
            onClick={() => push({ key: '', weight: 50 })}
            disabled={locked}
          >
            <PlusIcon className='h-4 w-4' aria-hidden='true' />
            Variante hinzufügen
          </button>
          <p className={hintClass}>
            Die erste Variante ist immer Control, also der heutige Stand.
            Gewichte werden umgerechnet und müssen nicht 100 ergeben.
          </p>
        </div>
      )}
    </FieldArray>
  )
}

const PercentField = () => (
  <div>
    <label htmlFor='percent' className={labelClass}>
      Ausgespielt an (%)
    </label>
    <div className='flex items-center gap-4'>
      <Field
        type='range'
        name='percent'
        min='0'
        max='100'
        step='1'
        className='flex-1 accent-indigo-600'
        aria-label='Prozent als Slider'
      />
      <Field
        type='number'
        id='percent'
        name='percent'
        min='0'
        max='100'
        step='1'
        className={classNames(inputClass, 'w-24')}
      />
      <span className='text-sm text-gray-500'>%</span>
    </div>
    <FieldError name='percent' />
    <p className={hintClass}>
      Hochdrehen behält alle, die schon drin sind. Rollouts werden nicht
      statistisch ausgewertet und lassen sich jederzeit ändern.
    </p>
  </div>
)

const TargetingFields = () => (
  <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
    <div>
      <span className={labelClass}>Stages</span>
      <div className='flex flex-wrap gap-4'>
        {STAGES.map((stage) => (
          <label
            key={stage}
            className='flex items-center gap-2 text-sm text-gray-700'
          >
            <Field
              type='checkbox'
              name='targeting.stages'
              value={stage}
              className={checkboxClass}
            />
            {stage}
          </label>
        ))}
      </div>
      <p className={hintClass}>
        Keine angehakt heißt alle Umgebungen, also auch production.
      </p>
    </div>
    <div>
      <span className={labelClass}>Lead-Ampel</span>
      <div className='flex flex-wrap gap-4'>
        {LEAD_AMPEL.map((ampel) => (
          <label
            key={ampel}
            className='flex items-center gap-2 text-sm text-gray-700'
          >
            <Field
              type='checkbox'
              name='targeting.lead_ampel'
              value={ampel}
              className={checkboxClass}
            />
            {ampel}
          </label>
        ))}
      </div>
      <p className={hintClass}>Nur im Dashboard bekannt.</p>
    </div>
    <div>
      <label htmlFor='targeting.partner_ids' className={labelClass}>
        Partner-IDs
      </label>
      <Field
        as='textarea'
        rows={2}
        id='targeting.partner_ids'
        name='targeting.partner_ids'
        className={classNames(inputClass, 'font-mono')}
        placeholder='Mehrere IDs mit Komma oder Zeilenumbruch trennen'
      />
      <p className={hintClass}>
        Nur im Dashboard bekannt, z. B. für Pilotkunden.
      </p>
    </div>
    <div>
      <label htmlFor='targeting.is_trial' className={labelClass}>
        Trial
      </label>
      <Field
        as='select'
        id='targeting.is_trial'
        name='targeting.is_trial'
        className={inputClass}
      >
        <option value=''>Egal</option>
        <option value='true'>Nur Trial</option>
        <option value='false'>Nur ohne Trial</option>
      </Field>
      <p className={hintClass}>Nur im Dashboard bekannt.</p>
    </div>
  </div>
)

const OverridesField = () => {
  const { values } = useFormikContext()
  const options =
    values.kind === KIND.ROLLOUT
      ? ROLLOUT_VARIANTS
      : values.variants.map((v) => (v.key || '').trim()).filter(Boolean)

  return (
    <FieldArray name='overrides'>
      {({ push, remove }) => (
        <div className='space-y-2'>
          {values.overrides.map((override, index) => (
            <div key={index} className='flex flex-wrap items-start gap-2'>
              <div className='min-w-[12rem] flex-1'>
                <Field
                  name={`overrides.${index}.unitId`}
                  className={classNames(inputClass, 'font-mono')}
                  placeholder={UNIT_ID_LABELS[values.unit] || 'Unit-ID'}
                  aria-label={UNIT_ID_LABELS[values.unit] || 'Unit-ID'}
                />
                <FieldError name={`overrides.${index}.unitId`} />
              </div>
              <div className='w-48'>
                <Field
                  as='select'
                  name={`overrides.${index}.variant`}
                  className={inputClass}
                  aria-label='Variante'
                >
                  <option value=''>Variante wählen</option>
                  {options.map((key) => (
                    <option key={key} value={key}>
                      {key}
                    </option>
                  ))}
                </Field>
                <FieldError name={`overrides.${index}.variant`} />
              </div>
              <button
                type='button'
                className={iconButtonClass}
                onClick={() => remove(index)}
                title='Override entfernen'
              >
                <TrashIcon className='h-4 w-4' aria-hidden='true' />
                <span className='sr-only'>Override entfernen</span>
              </button>
            </div>
          ))}
          <FieldError name='overrides' />
          <button
            type='button'
            className={addButtonClass}
            onClick={() => push({ unitId: '', variant: options[0] || '' })}
          >
            <PlusIcon className='h-4 w-4' aria-hidden='true' />
            Override hinzufügen
          </button>
        </div>
      )}
    </FieldArray>
  )
}

const NumberField = ({
  name,
  label,
  hint,
  help,
  disabled,
  step = '1',
  min = '0'
}) => (
  <div>
    <label htmlFor={name} className={labelClass}>
      {label}
    </label>
    <Field
      type='number'
      id={name}
      name={name}
      step={step}
      min={min}
      className={inputClass}
      disabled={disabled}
    />
    <FieldError name={name} />
    {hint && <p className={hintClass}>{hint}</p>}
    {help && <Explain className='mt-1'>{help}</Explain>}
  </div>
)

const DecisionFields = ({ locked }) => (
  <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
    <div>
      <label htmlFor='decision.primary' className={labelClass}>
        Primäre Metrik
      </label>
      <Field
        as='select'
        id='decision.primary'
        name='decision.primary'
        className={inputClass}
        disabled={locked}
      >
        {METRICS.map((metric) => (
          <option key={metric} value={metric}>
            {METRIC_LABELS[metric]} ({metric})
          </option>
        ))}
      </Field>
      <FieldError name='decision.primary' />
      <p className={hintClass}>Diese Metrik entscheidet über den Gewinner.</p>
      <Explain className='mt-1'>{HELP.primary}</Explain>
    </div>
    <NumberField
      name='decision.min_days'
      label='Mindestlaufzeit (min_days)'
      hint='Vorher wird nie entschieden. Standard 14 Tage.'
      help={HELP.minDays}
      disabled={locked}
    />
    <NumberField
      name='decision.max_days'
      label='Maximale Laufzeit (max_days)'
      hint='Danach endet der Test, ohne Gewinner bleibt Control.'
      help={HELP.maxDays}
      disabled={locked}
    />
    <NumberField
      name='decision.min_exposures_per_variant'
      label='Exposures je Variante (min_exposures_per_variant)'
      hint='Mindestens so viele Besucher je Variante.'
      help={HELP.minExposures}
      disabled={locked}
    />
    <NumberField
      name='decision.min_conversions_total'
      label='Conversions insgesamt (min_conversions_total)'
      hint='Mindestens so viele Conversions über alle Varianten.'
      help={HELP.minConversions}
      disabled={locked}
    />
    <NumberField
      name='decision.threshold'
      label='Schwelle P(best) (threshold)'
      step='0.005'
      min='0.5'
      disabled={locked}
      hint='0,99 heißt, eine Variante muss zu 99 % die beste sein.'
      help={HELP.threshold}
    />
  </div>
)

const JsonPreview = ({ baseFlag }) => {
  const { values } = useFormikContext()
  const flag = formValuesToFlag(values, baseFlag)
  return (
    <details className='rounded-md ring-1 ring-inset ring-gray-200'>
      <summary className='cursor-pointer px-3 py-2 text-sm font-medium text-gray-700'>
        JSON-Vorschau des Flags
      </summary>
      <div className='overflow-x-auto px-3 pb-3 text-xs'>
        <JSONTree data={flag} hideRoot shouldExpandNodeInitially={() => true} />
      </div>
    </details>
  )
}

const FlagFormBody = ({ isNew, baseFlag, locked, conflict, close }) => {
  const { values, isSubmitting, submitCount, isValid } = useFormikContext()
  const isExperiment = values.kind === KIND.EXPERIMENT

  return (
    <Form noValidate>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h3 className='text-base font-semibold leading-6 text-gray-900'>
            {isNew ? 'Neues Flag anlegen' : 'Flag bearbeiten'}
          </h3>
          {!isNew && (
            <p className='mt-1 font-mono text-sm text-gray-500'>
              {baseFlag?.key}
            </p>
          )}
        </div>
        {!isNew && <StatusBadge status={baseFlag?.status} />}
      </div>

      {conflict && (
        <Notice tone='warning' className='mt-4'>
          Inzwischen hat jemand die Flags geändert, z. B. der tägliche
          Evaluator. Der aktuelle Stand ist neu geladen, deine Eingaben sind
          noch nicht gespeichert. Prüf sie und speichere erneut.
        </Notice>
      )}

      {locked && <LockNotice className='mt-4' />}

      <div className='mt-5 space-y-5'>
        <Section title='Grunddaten'>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div>
              <label htmlFor='key' className={labelClass}>
                Key
              </label>
              {isNew ? (
                <KeyField />
              ) : (
                <input
                  id='key'
                  className={classNames(inputClass, 'font-mono')}
                  value={baseFlag?.key || ''}
                  disabled
                />
              )}
              <FieldError name='key' />
              <p className={hintClass}>
                {isNew
                  ? 'Nur Kleinbuchstaben, Ziffern und Unterstrich. Lässt sich später nicht ändern.'
                  : 'Der Key lässt sich nicht ändern.'}
              </p>
              <Explain className='mt-1'>{HELP.key}</Explain>
            </div>
            <div>
              <label htmlFor='owner' className={labelClass}>
                Owner
              </label>
              <Field
                id='owner'
                name='owner'
                className={inputClass}
                placeholder='z. B. florian'
              />
              <FieldError name='owner' />
              <p className={hintClass}>
                Wer den Test verantwortet und die Slack-Meldung bewertet.
              </p>
            </div>
            <div className='sm:col-span-2'>
              <label htmlFor='description' className={labelClass}>
                Beschreibung
              </label>
              <Field
                as='textarea'
                rows={2}
                id='description'
                name='description'
                className={inputClass}
                placeholder='Was wird getestet bzw. ausgerollt?'
              />
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
            <div>
              <span className={labelClass}>Art</span>
              <div className='flex gap-4 pt-1.5'>
                {Object.values(KIND).map((kind) => (
                  <label
                    key={kind}
                    className={classNames(
                      'flex items-center gap-2 text-sm',
                      locked ? 'text-gray-400' : 'text-gray-700'
                    )}
                  >
                    <Field
                      type='radio'
                      name='kind'
                      value={kind}
                      disabled={locked}
                      className='h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600'
                    />
                    {KIND_LABELS[kind]}
                  </label>
                ))}
              </div>
              <p className={hintClass}>
                {values.kind === KIND.ROLLOUT
                  ? 'Feature für x % einschalten, ohne Auswertung.'
                  : 'Varianten vergleichen und automatisch entscheiden.'}
              </p>
              <Explain className='mt-1'>{HELP.kind}</Explain>
            </div>
            <div>
              <label htmlFor='unit' className={labelClass}>
                Unit
              </label>
              <Field
                as='select'
                id='unit'
                name='unit'
                className={inputClass}
                disabled={locked}
              >
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {UNIT_LABELS[unit]}
                  </option>
                ))}
              </Field>
              <p className={hintClass}>
                Wer als eine Person zählt und immer dieselbe Variante sieht.
              </p>
              <Explain className='mt-1'>{HELP.unit}</Explain>
            </div>
            <div>
              <label htmlFor='surface' className={labelClass}>
                Surface
              </label>
              <Field
                as='select'
                id='surface'
                name='surface'
                className={inputClass}
                disabled={locked}
              >
                {SURFACES.map((surface) => (
                  <option key={surface} value={surface}>
                    {SURFACE_LABELS[surface]}
                  </option>
                ))}
              </Field>
              <p className={hintClass}>
                Wo das Flag wirkt und die Exposure gezählt wird.
              </p>
              <Explain className='mt-1'>{HELP.surface}</Explain>
            </div>
          </div>

          <div>
            <label htmlFor='salt' className={labelClass}>
              Salt
            </label>
            <Field
              id='salt'
              name='salt'
              className={classNames(inputClass, 'font-mono')}
              placeholder={values.key || 'Default ist der Key'}
              disabled={locked}
            />
            <FieldError name='salt' />
            <p className={hintClass}>
              Default ist der Key. Ein neuer Salt ergibt eine neue Verteilung.
            </p>
            <Explain className='mt-1'>{HELP.salt}</Explain>
          </div>
        </Section>

        {isExperiment ? (
          <Section title='Varianten' help={HELP.variants}>
            <VariantsField locked={locked} />
          </Section>
        ) : (
          <Section title='Ausspielung' help={HELP.percent}>
            <PercentField />
          </Section>
        )}

        <Section
          title='Targeting'
          description='Wer überhaupt teilnimmt. Leere Felder schränken nicht ein.'
          help={HELP.targeting}
        >
          <TargetingFields />
        </Section>

        <Section
          title='Overrides'
          description='Feste Zuteilung für einzelne Units, z. B. für QA. Zählt nie als Exposure.'
          help={HELP.overrides}
        >
          <OverridesField />
        </Section>

        {isExperiment && (
          <Section
            title='Entscheidung'
            description='Wird vor dem Start festgelegt und ist danach gesperrt.'
            help={HELP.decision}
          >
            {values.unit !== 'visitor' && (
              <Notice tone='info'>
                Der Evaluator wertet nur Experimente mit Unit visitor aus. Bei
                dieser Unit bleibt die Auswertung manuell.
              </Notice>
            )}
            <DecisionFields locked={locked} />
            <RuntimeEstimator
              variants={values.variants}
              decision={values.decision}
              surface={values.surface}
            />
          </Section>
        )}

        <Section
          title='Kontrolle'
          description='So wird das Flag gespeichert. Nur zum Nachsehen, hier musst du nichts tun.'
        >
          <JsonPreview baseFlag={baseFlag} />
        </Section>
      </div>

      {submitCount > 0 && !isValid && (
        <p className='mt-4 text-sm text-red-600'>
          Bitte prüf die markierten Felder.
        </p>
      )}

      <div className='mt-6 flex justify-end gap-3'>
        <Button.SecondaryLG
          text='Abbrechen'
          onClick={close}
          disabled={isSubmitting}
        />
        <Button.PrimaryLG
          type='submit'
          text={isNew ? 'Anlegen' : 'Speichern'}
          isLoading={isSubmitting}
        />
      </div>
    </Form>
  )
}

// `session` friert den Stand beim Öffnen ein. Sein updatedAt ist die
// Bedingung beim Speichern, damit zwischenzeitliche Änderungen auffallen.
const FlagForm = ({ session, close, saveFlags, onSaved }) => {
  const { success, error } = useNotificationContext()
  const [base, setBase] = useState(session.base)
  const [conflict, setConflict] = useState(false)

  const isNew = !session.flagKey
  const stored = isNew ? null : base.flags[session.flagKey]
  const baseFlag = stored ? { ...stored, key: session.flagKey } : null
  const locked = isLocked(baseFlag)

  const [initialValues] = useState(() =>
    baseFlag ? flagToFormValues(baseFlag, session.flagKey) : emptyFormValues()
  )

  const validationSchema = useMemo(
    () =>
      buildFlagSchema({
        existingKeys: Object.keys(base.flags),
        isNew,
        locked
      }),
    [base, isNew, locked]
  )

  const onSubmit = async (values, { resetForm }) => {
    const key = isNew ? values.key.trim() : session.flagKey
    try {
      const flag = formValuesToFlag(values, baseFlag)
      await saveFlags(base, { ...base.flags, [key]: flag })
      success(isNew ? 'Flag angelegt' : 'Flag gespeichert')
      close()
      if (onSaved) onSaved(key)
    } catch (err) {
      // Bestätigung für production abgebrochen, das Formular bleibt offen
      if (err instanceof PricingWriteCancelledError) {
        error(err.message)
        return
      }
      if (!(err instanceof FlagsConflictError)) {
        error(`Speichern fehlgeschlagen. ${extractErrorMessage(err)}`)
        return
      }

      setBase(err.fresh)
      setConflict(true)
      error(CONFLICT_MESSAGE)

      // Formular auf den frischen Stand setzen. Mit den alten Werten wuerde
      // ein erneutes Speichern die parallele Aenderung ueberschreiben, genau
      // das, was der Konfliktschutz verhindern soll. Eigene Aenderungen
      // traegt der Nutzer danach bewusst neu ein.
      const freshFlag = !isNew && err.fresh.flags[key]
      if (freshFlag)
        resetForm({ values: flagToFormValues({ ...freshFlag, key }, key) })
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <FlagFormBody
        isNew={isNew}
        baseFlag={baseFlag}
        locked={locked}
        conflict={conflict}
        close={close}
      />
    </Formik>
  )
}

const FlagFormModal = ({ open, setOpen, session, saveFlags, onSaved }) => (
  <Modal open={open} setOpen={setOpen} size='4xl'>
    {session && (
      <FlagForm
        key={session.id}
        session={session}
        close={() => setOpen(false)}
        saveFlags={saveFlags}
        onSaved={onSaved}
      />
    )}
  </Modal>
)

export default FlagFormModal
